import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useOpticalProcedureCopywriting } from '@/app/_lens-components/copywriting/CopywritingProvider';
import { getOpticalProcedureViewAreaTitle } from '@/app/_lens-components/copywriting/normalize';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';
import { useDefaultSelection } from '@/app/_lens-components/DefaultSelectionContext';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { resolveLensTypeTrackingFields } from '@/app/_lens-components/tracking';
import { ELensViewAreaType, LensIndexItem } from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  getAdvancedLensUpgradePriceInfo,
  handleColorItemPriceInfo,
  handleLensIndexPriceInfo,
} from '@/app/_lens-components/utils';
import Image from '@/components/Image';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { processLineBreaks } from '@/utils/tools';
import { mergePrevLensSelection } from '@/app/_lens-components/utils/operatePrevLensSelection';
import { useTranslations } from '@/i18n/use-translations';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useId, useEffect, useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_UPGRADE_PRICE_INFO = {
  applyMembershipLevel: -1,
  price: '0.00',
  promotionDisplayLabel: '',
  sellPrice: '0.00',
};

export default function LensViewArea({
  progressiveViewArea,
  selectedLensIndexItem,
  selectedLensViewAreaType,
  onSelectedLensViewAreaType,
  onSelectedAdvancedLensIndexItem,
}: {
  progressiveViewArea: GetResponseType<
    ApiResponseDefinition['GET:/api/lens/view_area']
  >['view_area'];
  selectedLensIndexItem: LensIndexItem;
  selectedLensViewAreaType: ELensViewAreaType;
  onSelectedLensViewAreaType: (type: ELensViewAreaType) => void;
  onSelectedAdvancedLensIndexItem: (item: LensIndexItem) => void;
}) {
  const t = useTranslations('lens-process');
  const copywriting = useOpticalProcedureCopywriting();
  const viewAreaTitle = getOpticalProcedureViewAreaTitle(
    copywriting,
    progressiveViewArea?.visual_field_title || '',
  );
  const recommendedTitle = useOpticalProcedureTitle('recommended', t('recommendedForYou'));
  const { canModifyPrevSelection } = useDefaultSelection();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const waiMode = useStore((state) => state.waiMode);
  const { trackEvent } = useReporting();
  const {
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    selectedLensType,
    selectedBrand,
    selectedColorItem,
    selectedAdvancedLensIndexItem,
    lensTypeForTracking,
  } = useLensProcessStore();

  const [swiperData, setSwiperData] = useState<
    Array<{
      type: string;
      recommend: number;
      progressive_name: string;
      img: string;
      description: string;
    }>
  >([]);
  const [upgradePriceInfo, setUpgradePriceInfo] = useState(DEFAULT_UPGRADE_PRICE_INFO);
  const viewAreaListRef = useRef<HTMLUListElement | null>(null);

  const viewAreaGroupLabelId = useId();
  const viewAreaRadioName = useId();

  useLayoutEffect(() => {
    if (!selectedLensViewAreaType) return;
    const selectedEl = viewAreaListRef.current?.querySelector<HTMLElement>(
      `[data-view-area-type="${selectedLensViewAreaType}"]`,
    );
    selectedEl?.scrollIntoView({
      block: 'center',
      behavior: 'auto',
      inline: 'nearest',
    });
  }, [selectedLensViewAreaType, swiperData.length]);

  useEffect(() => {
    const advancedLensIndexItem = selectedLensIndexItem?.advanced;
    if (advancedLensIndexItem) {
      setUpgradePriceInfo({
        ...getAdvancedLensUpgradePriceInfo({
          advancedLensIndexItem,
          currency,
          selectedColorItem,
          selectedLensIndexItem,
          userInfo,
        }),
      });
    } else {
      setUpgradePriceInfo(DEFAULT_UPGRADE_PRICE_INFO);
    }
  }, [selectedLensIndexItem, selectedColorItem, userInfo, currency]);

  // 读取高级视野 advanced 字段价格

  // 初始化数据
  useEffect(() => {
    if (progressiveViewArea) {
      buildDataAction();
    }
  }, [progressiveViewArea]);

  // 初始化轮播数据
  const buildDataAction = () => {
    const viewData = progressiveViewArea;

    const ret = [
      {
        type: ELensViewAreaType.NORMAL,
        recommend: 0, // 1： 推荐， 2：不推荐
        progressive_name: viewData.progressive_name,
        img: viewData.progressive_img_src,
        description: viewData.progressive_description,
      },
      {
        type: ELensViewAreaType.ADVANCED,
        recommend: viewData.recommend, // 1： 推荐， 2：不推荐
        progressive_name: viewData.advance_progressive_name,
        img: viewData.advance_progressive_img_src,
        description: viewData.advance_progressive_description,
      },
    ];

    // 根据排序设置数据
    setSwiperData(viewData.sort === 1 ? ret : ret.reverse());
  };

  // 处理项目点击
  const onItemClick = (item: any) => {
    // 如果点击的是已选中项，不做处理
    if (item.type === selectedLensViewAreaType) {
      return;
    }

    onSelectedAdvancedLensIndexItem(
      item.type === ELensViewAreaType.ADVANCED ? selectedLensIndexItem.advanced : null,
    );
    onSelectedLensViewAreaType(item.type);
    if (canModifyPrevSelection) {
      mergePrevLensSelection({ prevViewArea: item.type });
    }

    SDViewAreaPageSelectClick(item.type === ELensViewAreaType.ADVANCED);
  };

  useEffectWithCurReady(() => {
    // 埋点：页面浏览
    try {
      const { index_name } = selectedLensIndexItem;
      const { price: lensIndexPrice, sell_price: lensIndexSellPrice } = handleLensIndexPriceInfo(
        selectedLensIndexItem,
        currency,
        userInfo,
      );
      const { price, sell_price } = handleColorItemPriceInfo(
        selectedColorItem,
        false,
        currency,
        userInfo,
      );
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: selectedLensType,
        brand: selectedBrand,
        advancedLensIndexItem: selectedAdvancedLensIndexItem,
        lensTypeForTracking,
      });
      trackEvent(
        {
          eventName: 'ViewAreaPage_View',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: selectedColorItem?.color_name,
            lens_id: `${selectedLensIndexItem.id}`,
            lens_index: selectedLensIndexItem.refractive_index,
            lens_code: selectedLensIndexItem.code,
            lens_index_name: index_name,
            lens_price: parseFloat(price),
            current_lens_price: parseFloat(sell_price),
            lens_index_price: parseFloat(lensIndexPrice),
            current_lens_index_price: parseFloat(lensIndexSellPrice),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }, [userInfo]);

  function SDViewAreaPageSelectClick(isAdvanced: boolean) {
    try {
      const advancedLensIndexItem = isAdvanced ? selectedLensIndexItem.advanced : null;
      const colorItem = isAdvanced ? selectedLensIndexItem.advanced : selectedColorItem;
      const lensIndexItem = isAdvanced ? selectedLensIndexItem.advanced : selectedLensIndexItem;
      const lensIndexPriceInfo = handleLensIndexPriceInfo(lensIndexItem, currency, userInfo);
      const colorItemPriceInfo = handleColorItemPriceInfo(colorItem, false, currency, userInfo);
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: selectedLensType,
        brand: selectedBrand,
        advancedLensIndexItem,
        lensTypeForTracking,
      });

      trackEvent(
        {
          eventName: 'ViewAreaPage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            lens_id: `${lensIndexItem.id}`,
            lens_price: parseFloat(colorItemPriceInfo.price),
            current_lens_price: parseFloat(colorItemPriceInfo.sell_price),
            lens_index_name: lensIndexItem.index_name,
            lens_index: lensIndexItem.refractive_index,
            lens_code: lensIndexItem.code,
            lens_index_price: parseFloat(lensIndexPriceInfo.price),
            current_lens_index_price: parseFloat(lensIndexPriceInfo.sell_price),
            currency: currency.name,

            prescription_type_name: presTypeOptionName,
            prescription_type: getApiPrescriptionType(prescriptionType),
            ...lensTypeTrackingFields,
            lens_color: selectedColorItem?.color_name,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  // 渲染空视图
  const renderEmptyView = () => (
    <div className={stepCommonClassName.emptyState}>
      <Image src="/lens-process/null.png" width={118} height={118} alt={t('empty')} />
      <p className={stepCommonClassName.emptyStateText}>{t('sorryNoSuitableLensFound')}</p>
      <button className={stepCommonClassName.emptyStateButton}>{t('editPrescription')}</button>
    </div>
  );

  return (
    <div className="overflow-y-hidden h-full box-border font-br">
      <h3 id={viewAreaGroupLabelId} className={stepCommonClassName.stepTitle}>
        <span>{viewAreaTitle}</span>
      </h3>

      {Array.isArray(swiperData) && swiperData.length > 0 ? (
        <div role="radiogroup" aria-labelledby={viewAreaGroupLabelId}>
          <ul className={stepCommonClassName.optionsContainer} ref={viewAreaListRef}>
            {swiperData.map((item, index) => {
              const isSelected = selectedLensViewAreaType === item.type;
              const optionId = `${viewAreaRadioName}-${item.type}`;
              const optionTitleId = `${optionId}-title`;
              const optionDescriptionId = `${optionId}-description`;
              const recommendBadgeId = `${optionId}-recommend`;
              const describedByIds = [
                optionDescriptionId,
                item.recommend === 1 ? recommendBadgeId : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <li
                  key={index}
                  data-view-area-type={item.type}
                  className={`${stepCommonClassName.optionItem} ${
                    item.recommend === 1 ? stepCommonClassName.recommendedItem : ''
                  }`}
                >
                  {item.recommend === 1 && (
                    <div id={recommendBadgeId} className={stepCommonClassName.recommendBadge}>
                      <div className={stepCommonClassName.recommendBadgeContent}>
                        <span>{recommendedTitle}</span>
                      </div>
                    </div>
                  )}

                  <div
                    className={`${stepCommonClassName.optionBox} ${
                      isSelected ? stepCommonClassName.selectedOptionBox : ''
                    }`}
                  >
                    <input
                      id={optionId}
                      className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0"
                      type="radio"
                      name={viewAreaRadioName}
                      checked={isSelected}
                      onChange={() => onItemClick(item)}
                      aria-labelledby={optionTitleId}
                      aria-describedby={describedByIds}
                    />
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 z-[1] rounded-[6px] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-purple"
                    />

                    {item.type === ELensViewAreaType.ADVANCED &&
                      upgradePriceInfo.promotionDisplayLabel && (
                        <div
                          className={`${stepCommonClassName.discountBadge(
                            upgradePriceInfo.applyMembershipLevel,
                          )} flex items-center justify-center !leading-none pointer-events-none`}
                        >
                          <span className="leading-none">
                            {upgradePriceInfo.promotionDisplayLabel}
                          </span>
                        </div>
                      )}

                    <div
                      className={`${stepCommonClassName.optionContent} !items-center pointer-events-none`}
                    >
                      <div className={`${stepCommonClassName.imageContainer} min-w-[100px]`}>
                        <Image
                          src={item.img}
                          className="w-[100px] h-[100px] object-contain"
                          width={100}
                          height={100}
                          alt={item.progressive_name}
                        />
                      </div>

                      <div className={stepCommonClassName.textContainer}>
                        <div className={stepCommonClassName.textCenterContainer}>
                          <p id={optionTitleId} className={stepCommonClassName.titleContainer}>
                            {item.progressive_name}
                          </p>
                          <p
                            id={optionDescriptionId}
                            className={stepCommonClassName.description(waiMode)}
                            dangerouslySetInnerHTML={{
                              __html: processLineBreaks(item.description),
                            }}
                          />
                        </div>

                        <div className={stepCommonClassName.priceContainer}>
                          <span className={stepCommonClassName.priceInnerBox}>
                            <span className={stepCommonClassName.priceText}>
                              {item.type === ELensViewAreaType.NORMAL
                                ? t('free')
                                : formatPriceNumberWithSymbol(upgradePriceInfo.sellPrice, currency)}
                            </span>
                            {item.type === ELensViewAreaType.ADVANCED &&
                              upgradePriceInfo.price !== upgradePriceInfo.sellPrice && (
                                <span className={stepCommonClassName.originalPriceText(waiMode)}>
                                  {formatPriceNumberWithSymbol(upgradePriceInfo.price, currency)}
                                </span>
                              )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        renderEmptyView()
      )}
    </div>
  );
}
